import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Loader } from '@components/feed/Loader';
import { PortalCompetitionSession } from '@api/competitionsApi/interfaces';
import { PortalSessionListItem } from '../../components/PortalSessionListItem';
import { partitionPortalSessions } from '../../sessionHelpers';
import { ISessionsTab } from './interfaces';

export const SessionsTab: React.FC<ISessionsTab> = ({
  show,
  sessions,
  isLoading,
  onCompletedSessionClick,
}): JSX.Element | null => {
  const { t } = useTranslation();

  const { upcoming, past } = useMemo(
    () => partitionPortalSessions(sessions),
    [sessions]
  );

  if (!show) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (sessions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('competitions:portal.details.sessionsTab.empty')}
      </Typography>
    );
  }

  const renderSessionList = (
    items: PortalCompetitionSession[],
    showParticipantCount: boolean
  ): JSX.Element => (
    <Stack sx={{ gap: 1 }}>
      {items.map((session) => (
        <PortalSessionListItem
          key={session.id}
          session={session}
          onCompletedSessionClick={onCompletedSessionClick}
          showParticipantCount={showParticipantCount}
        />
      ))}
    </Stack>
  );

  const renderSection = (
    titleKey: string,
    emptyKey: string,
    items: PortalCompetitionSession[],
    showParticipantCount: boolean
  ): JSX.Element => (
    <Box>
      <Typography
        variant="subtitle2"
        component="h3"
        sx={{ fontWeight: 600, mb: 1.5, fontSize: '.95rem' }}
      >
        {t(titleKey)}
      </Typography>
      {items.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '.85rem' }}
        >
          {t(emptyKey)}
        </Typography>
      ) : (
        renderSessionList(items, showParticipantCount)
      )}
    </Box>
  );

  return (
    <Stack spacing={3}>
      {renderSection(
        'competitions:portal.details.sessionsTab.sections.upcoming',
        'competitions:portal.details.sessionsTab.sections.upcomingEmpty',
        upcoming,
        false
      )}
      {renderSection(
        'competitions:portal.details.sessionsTab.sections.past',
        'competitions:portal.details.sessionsTab.sections.pastEmpty',
        past,
        true
      )}
    </Stack>
  );
};
